import React, { useState, useRef, useEffect } from "react";
import {
  ModalOverlay,
  UploadContent,
  UploadTxt,
  ModalBtn,
  DragAndDrop,
  Icon,
  UploadText,
  SupportText,
  FilesDiv,
} from "./Style";
import CloseX from "../../assets/x-close.svg";
import UploadIcon from "../../assets/upload.svg";
import BlueCross from "../../assets/blue-cross.svg";
import FileIcon from "../../assets/file-icon.svg";
import { PDFDocument } from "pdf-lib";

import axios from "axios";
import { CircularProgress } from "@mui/material";
import SnackbarAlert from "../../Components/Alerts/SnackbarAlert";
import {
  MAX_FILE_MB,
  MAX_FILE_SIZE,
  MAX_TOTAL_SIZE_MB,
  API_BASE_URL,
} from "../../constant/constant";
import { filterCorruptFile } from "../Environment/Asconfig";

const UploadModal = ({ onClose, currFileUrls, onUpdate, totalFileSizeMB }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const dragDropRef = useRef(null);

  // Calculate the total size of selected files in MB
  const calculateSelectedFilesSizeMB = (files) => {
    return files.reduce((total, file) => total + file.size, 0) / 1024 / 1024;
  };

  const handleFileChange = async (event) => {
    const input = event.target;

    // Convert FileList → Array and normalize filenames
    const files = Array.from(input.files).map((file) => {
      const newFileName = file.name.replace(/\s+/g, "-");
      return new File([file], newFileName, { type: file.type });
    });

    // 🔥 CRITICAL: reset input so selecting the same file triggers onChange again
    input.value = "";

    // 1️⃣ Single file size validation
    const largeFile = files.find((file) => file.size > MAX_FILE_SIZE);
    if (largeFile) {
      setSnackbarMessage(`File size exceeds ${MAX_FILE_MB}MB`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // 2️⃣ Check corrupted PDFs
    const { validFiles, corruptedFiles } = await filterValidPDFs(files);

    if (corruptedFiles.length > 0) {
      setSnackbarMessage(
        `The following PDF files are corrupted or unreadable: ${corruptedFiles
          .map((f) => f.name)
          .join(", ")}`,
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }

    // Stop if nothing valid remains
    if (validFiles.length === 0) return;

    // 3️⃣ Total size validation
    const newTotalSizeMB =
      calculateSelectedFilesSizeMB([...selectedFiles, ...validFiles]) +
      parseFloat(totalFileSizeMB);

    if (newTotalSizeMB > MAX_TOTAL_SIZE_MB) {
      setSnackbarMessage(
        `Data exceeded ${MAX_TOTAL_SIZE_MB}MB. Delete some files to continue`,
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // 4️⃣ All checks passed → add files
    setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDropRef.current.style.border = "2px solid #51307F";
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDropRef.current.style.border = "2px dashed #cccccc";
  };

  // 🧠 Helper: Separate valid and corrupted PDFs
  const filterValidPDFs = async (files) => {
    const validFiles = [];
    const corruptedFiles = [];

    for (const file of files) {
      // Only check PDFs — all other files are considered valid
      if (file.type === "application/pdf") {
        const isValid = await isReadablePDF(file);
        if (isValid) validFiles.push(file);
        else corruptedFiles.push(file);
      } else {
        validFiles.push(file);
      }
    }

    return { validFiles, corruptedFiles };
  };

  // 🔍 Try to load the PDF with pdf-lib — fails if corrupted
  const isReadablePDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      return true;
    } catch (error) {
      console.error(`Corrupted PDF detected: ${file.name}`, error);
      return false;
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDropRef.current.style.border = "2px dashed #cccccc";

    const files = Array.from(event.dataTransfer.files).map((file) => {
      const newFileName = file.name.replace(/\s+/g, "-");
      return new File([file], newFileName, { type: file.type });
    });

    const largeFile = files.find((file) => file.size > MAX_FILE_SIZE);
    if (largeFile) {
      setSnackbarMessage(`File size exceeds ${MAX_FILE_MB}MB`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // 🔍 Check for corrupted files before proceeding
    const { validFiles, corruptedFiles } = await filterValidPDFs(files);
    if (corruptedFiles.length > 0) {
      setSnackbarMessage(
        `The following files appear to be corrupted or unreadable: ${corruptedFiles
          .map((f) => f.name)
          .join(", ")}`,
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    // If no valid files remain, stop here
    if (validFiles.length === 0) return;

    const newTotalSizeMB =
      calculateSelectedFilesSizeMB([...selectedFiles, ...validFiles]) +
      parseFloat(totalFileSizeMB);
    if (newTotalSizeMB > MAX_TOTAL_SIZE_MB) {
      setSnackbarMessage("Data exceeded 200MB. Delete some files to continue");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } else {
      setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    }
  };

  const handleRemoveFile = (fileName) => {
    const updatedFiles = selectedFiles.filter((file) => file.name !== fileName);
    setSelectedFiles(updatedFiles);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setSnackbarMessage("No files selected for upload");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setLoading(true);
    const signedUrls = [];
    const id = localStorage.getItem("id");

    // Helper function to sanitize filename
    const sanitizeFileName = (fileName) => {
      // Get file extension
      const lastDotIndex = fileName.lastIndexOf(".");
      const name =
        lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
      const extension =
        lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";

      // Remove extra whitespace and trim
      let sanitized = name.replace(/\s+/g, " ").trim();

      // Remove special characters except hyphens and underscores
      // Keep alphanumeric characters, hyphens, underscores, and spaces
      sanitized = sanitized.replace(/[^a-zA-Z0-9\-_ ]/g, "");

      // Replace remaining spaces with hyphens
      sanitized = sanitized.replace(/\s+/g, "-");

      // Remove consecutive hyphens or underscores
      sanitized = sanitized.replace(/[-_]{2,}/g, "-");

      // Remove leading/trailing hyphens or underscores
      sanitized = sanitized.replace(/^[-_]+|[-_]+$/g, "");

      return sanitized + extension;
    };

    for (const file of selectedFiles) {
      try {
        // Sanitize the filename
        const sanitizedFileName = sanitizeFileName(file.name);

        // Add timestamp to ensure uniqueness
        const fileExtension = sanitizedFileName.substring(
          sanitizedFileName.lastIndexOf("."),
        );
        const fileNameWithoutExt = sanitizedFileName.substring(
          0,
          sanitizedFileName.lastIndexOf("."),
        );
        const uniqueFileName = `${fileNameWithoutExt}${fileExtension}`;

        const response = await axios.get(
          `${API_BASE_URL}/api/auth/get-signed-url`,
          {
            params: { userId: id, fileName: uniqueFileName },
          },
        );

        if (response.data && response.data.signedUrl) {
          await axios.put(response.data.signedUrl, file, {
            headers: {
              "Content-Type": file.type,
              "x-amz-acl": "public-read",
            },
          });

          signedUrls.push({
            fileUrl: response.data.key,
            fileName: file.name, // Keep original filename for display
            fileSize: file.size,
            uploadTime: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error("Error uploading file", error);
      }
    }

    try {
      // Call set-training-status API
      await axios.post(
        `${API_BASE_URL}/api/auth/set-training-status`,
        {
          user_id: id,
          isTrained: false,
          trainingStatus: "pending",
        },
      );

      // Prepare payload for the embeddings API
      const embeddingResponse = await axios.post(
        "https://flowproai.aivoria.nl/ai_flow_pro/create_embeddings_multidoc",
        {
          user_id: id,
          uploaded_urls: [
            ...currFileUrls, // Include currFileUrls
            ...signedUrls.map((file) => file.fileUrl), // Include new file URLs
          ],
        },
      );

      console.log("files", embeddingResponse);

      if (
        embeddingResponse.data.message ===
        "Embeddings are being created in the background."
      ) {
        // Call upload-files API to save file data
        await axios.post(`${API_BASE_URL}/api/auth/upload-files`, {
          user_id: id,
          files: signedUrls, // Only include newly uploaded files here
        });
      }
      onUpdate();
      setSnackbarMessage("File/s uploaded successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage("Failed to upload files");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }

    setLoading(false);
    setTimeout(() => {
      setSnackbarOpen(false);
      onClose();
    }, 2000);
  };

  return (
    <ModalOverlay>
      <UploadContent>
        <div style={{ display: "flex", width: "100%" }}>
          <UploadTxt>Upload your Data files</UploadTxt>
          <img
            src={CloseX}
            alt="close"
            style={{ marginLeft: "auto", cursor: "pointer" }}
            onClick={onClose}
          />
        </div>
        <DragAndDrop
          ref={dragDropRef}
          onClick={() => document.getElementById("fileInput").click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            id="fileInput"
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            style={{ display: "none" }}
            onChange={handleFileChange}
            multiple
          />
          <Icon>
            <img src={UploadIcon} alt="upload" />
          </Icon>
          <UploadText>
            Drag and drop files, or
            <span style={{ color: "#51307F" }}> Browse</span>
          </UploadText>
          <SupportText>Supports pdf, doc, txt files</SupportText>
          <SupportText style={{ marginTop: "1.5rem" }}>
            Max files data limit: 200Mb{" "}
          </SupportText>
          <SupportText>Max single file size: 50Mb </SupportText>
        </DragAndDrop>
        {selectedFiles.length > 0 && (
          <>
            <UploadText style={{ marginTop: "0rem", color: "#8b9ab1" }}>
              Uploaded Documents
            </UploadText>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: "0.4rem",
              }}
            >
              {selectedFiles.map((file, index) => (
                <FilesDiv key={index}>
                  <img
                    src={FileIcon}
                    alt="file"
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                      marginRight: "0.5rem",
                    }}
                  />
                  {file.name}
                  <img
                    src={BlueCross}
                    alt="remove"
                    style={{
                      width: "1.2rem",
                      height: "1.2rem",
                      marginLeft: "auto",
                      cursor: "pointer",
                    }}
                    onClick={() => handleRemoveFile(file.name)}
                  />
                </FilesDiv>
              ))}
            </div>
          </>
        )}
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}
        >
          <UploadTxt
            style={{
              lineHeight: "1.5rem",
              fontWeight: "600",
              fontSize: "1rem",
              cursor: "pointer",
            }}
            onClick={onClose}
          >
            Skip
          </UploadTxt>
          <ModalBtn onClick={handleUpload}>
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Upload"
            )}
          </ModalBtn>
        </div>
      </UploadContent>
      <SnackbarAlert
        open={snackbarOpen}
        setOpen={setSnackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </ModalOverlay>
  );
};

export default UploadModal;
